import type { ComponentProps, ReactNode } from 'react';
import { useState } from 'react';

import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

import { useIsTruncated } from './useIsTruncated';

type TooltipPlacement = ComponentProps<typeof Tooltip>['placement'];

interface TruncatedNodeProps {
  /** Already-rendered content to clip to a single line. */
  children: ReactNode;
  /** Classes for the single-line, truncating element. */
  className?: string;
  /** Tooltip placement. @default 'top' */
  placement?: TooltipPlacement;
  /** Classes for the wrapper that slots into the parent layout. */
  wrapperClassName?: string;
}

/**
 * `TruncatedText`'s counterpart for content that is already a `ReactNode`.
 *
 * Use `TruncatedText` whenever you have the string — it is simpler and its
 * tooltip is exactly the value you passed. Reach for this only where the value
 * arrives pre-rendered and the raw text is genuinely not available at the call
 * site: the proposal diff cells, for instance, receive a formatted
 * `PropertyValueLabel` (dates as dates, currency as currency), and re-deriving
 * that string from the raw value would mean duplicating every formatter.
 *
 * The tooltip text is read from the rendered DOM (`textContent`) at measure
 * time, so it shows exactly what the user sees — formatting included — rather
 * than an unformatted echo of the underlying value.
 *
 * Like `TruncatedText`, the tooltip is enabled ONLY while the content is
 * actually clipped, and the wrapper must sit in a width-constrained context
 * for truncation to engage.
 */
export function TruncatedNode({
  children,
  className,
  placement = 'top',
  wrapperClassName = 'block min-w-0 max-w-full',
}: TruncatedNodeProps) {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>([children]);
  const [title, setTitle] = useState('');

  // Read the rendered text only when it is clipped — that is the only time the
  // tooltip can show, so there is no reason to touch the DOM otherwise.
  if (isTruncated) {
    const rendered = ref.current?.textContent?.trim() ?? '';
    if (rendered && rendered !== title) {
      setTitle(rendered);
    }
  }

  return (
    <Tooltip
      title={title}
      placement={placement}
      isDisabled={!isTruncated || !title}
      interactive
      wrapperClassName={wrapperClassName}
    >
      <span
        ref={ref}
        className={cx('block truncate', className)}
        tabIndex={isTruncated ? 0 : undefined}
      >
        {children}
      </span>
    </Tooltip>
  );
}

export default TruncatedNode;
